import React from 'react';
import { communitiesApi } from 'apis/CompanyAPI/communities/communitiesApi';
import useResourceLegacy from 'util/resource/useResourceLegacy';
import { CommunityConfiguration, TinyCommunity } from 'types/company/community';
import { ICompany } from 'types/company/company';
import { getOrElse } from 'util/resource';
import CommunityCompanyLabels from 'domain/communities/CompanyDialog/Labels/CommunityCompanyLabels';
import CommunityNotes from 'domain/communities/CompanyNotes/CommunityNotes';
import CommunityRooms from 'domain/communities/rooms';
import TransferToCommunity from '../../InnovasjonNorge/TransferToCommunity';
import DealLeads from './DealLeads/DealLeads';
import Mentorships from './Mentorships';
import { isInnovasjonNorge } from 'domain/communities/InnovasjonNorge/utils';
import { HasScope } from 'hooks/useCommunityRole';
import { useCommunityCompanyLabels } from 'apis/CompanyAPI/communities/useCommunityLabels';
import { invalidateStartsWith, invalidateWithReplacement } from 'hooks/useSWR';
import { applicationUrls } from 'apis/CompanyAPI/communities/applications/communityApplicationsApi';
import { groupsUrls } from 'apis/CompanyAPI/communities/applications/communityGroupsApi';

interface Props {
  companyId: number;
  community: TinyCommunity;
  companyProfile: ICompany;
  discussionUrl: (discussionId: string) => string;
  hasScopeInCommunity: HasScope;
  communityConfiguration: CommunityConfiguration;
}

export default function Sidebar(props: Props) {
  const { community, companyId, companyProfile, communityConfiguration } = props;
  const hasScope = props.hasScopeInCommunity;

  const [dealLeadsResource, , , reloadDealLeads] = useResourceLegacy(
    () => communitiesApi.dealLeads.list(props.community.slug, props.companyId),
    [props.community.slug, props.companyId],
  );

  const canSeeLabels = hasScope('Read', 'Community.Labels');
  const { mutate: refreshLabels } = useCommunityCompanyLabels(canSeeLabels, props.community.id, props.companyId);

  const handleLabelChange = () => {
    // Invalidate application companies list to refresh label chips in groups view
    invalidateStartsWith([applicationUrls.listCompanies(props.community.id)]);
    // Invalidate groups companies lists to refresh label chips in all groups
    invalidateWithReplacement([groupsUrls.listCompanies(props.community.id, '<replace-me>')], '<replace-me>');
  };

  return (
    <div className="u-content-spacing-top">
      {isInnovasjonNorge(props.community.communityType) && (
        <TransferToCommunity
          companyName={props.companyProfile.name}
          community={props.community}
          companyId={props.companyId}
          refreshLabels={refreshLabels}
        />
      )}
      {hasScope('Write', 'Community') && (
        <Mentorships community={community} companyId={companyProfile.id} companyName={companyProfile.name} />
      )}
      {hasScope('Write', 'Community') && communityConfiguration?.canAssignRooms && (
        <div className="u-content-spacing-bottom">
          <CommunityRooms
            community={community}
            communityConfiguration={communityConfiguration}
            company={props.companyProfile}
          />
        </div>
      )}
      {communityConfiguration?.hasDealLeads && (
        <div className="u-content-spacing-bottom">
          <DealLeads
            community={community}
            companyId={companyId}
            refresh={reloadDealLeads}
            dealLeads={getOrElse(dealLeadsResource, { values: [] }).values}
            hasScopeInCommunity={props.hasScopeInCommunity}
          />
        </div>
      )}
      {canSeeLabels && (
        <div className="u-content-spacing-bottom">
          <CommunityCompanyLabels
            communitySlug={props.community.slug}
            communityId={props.community.id}
            companyId={props.companyProfile.id}
            companyName={props.companyProfile.name}
            onLabelChange={handleLabelChange}
          />
        </div>
      )}
      {hasScope('Read', 'Community.CompanyNotes') && communityConfiguration?.hasCompanyNotes && (
        <div className="u-content-spacing-bottom">
          <CommunityNotes community={community} companyId={companyId} communityConfiguration={communityConfiguration} />
        </div>
      )}
    </div>
  );
}
